import clsx from 'clsx';
import React from 'react';
import { Link } from 'react-router-dom';
import { Button, type ButtonProps } from './Button';

/**
 * Public shell: the one layout for every page outside the app frame — sign
 * in, forgot / reset password, store onboarding, and later opt-in, legal and
 * 404.
 *
 * Anatomy (top to bottom): header (brand left, one quiet aside right) ·
 * optional progress slot · `main` on `surface` with one hairline · footer in
 * `fg-4` 11px. No sidebar, no PageHeader, no store switcher, no theme toggle
 * (follows the `.dark` class like everything else).
 *
 * Rules: the brand leads; one primary per screen (`PublicPrimary`: 40px,
 * full width below `sm`); one way to reach a person; no analytics or
 * third-party scripts. Errors are a danger `Callout` above the primary;
 * success is a calm screen with one action.
 *
 * `PublicPage` is the `.av2` root for pages that don't already have one.
 */
export const PublicPage: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div className={clsx('av2 min-h-screen bg-v2-canvas text-v2-fg', className)}>
    {children}
  </div>
);

export const AskArthurMark: React.FC<{ className?: string }> = ({
  className,
}) => (
  <span
    className={clsx('inline-flex items-center', className)}
    role="img"
    aria-label="AskArthur"
  >
    <img
      src="/images/logo/logo-full.svg"
      alt=""
      className="h-[22px] w-auto dark:hidden"
    />
    <img
      src="/images/logo/logo-full-dark.png"
      alt=""
      className="hidden h-[22px] w-auto dark:block"
    />
  </span>
);

export const PublicShell: React.FC<{
  /** Brand slot; defaults to the AskArthur mark. */
  brand?: React.ReactNode;
  /** Quiet right-hand header note. */
  aside?: React.ReactNode;
  /** Progress strip between header and main. */
  progress?: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * `form` 440px for a short form (sign in) — sits at its natural height
   * with the footer pinned to the bottom; `narrow` 600px for a
   * document-like page; `wide` 880px for a stepped intake with a side nav;
   * `checkout` 1040px with an unboxed main, for a two-column page whose
   * right column is a `Receipt` — the receipt is then the only box.
   */
  width?: 'form' | 'narrow' | 'wide' | 'checkout';
  /** Remove the default padding on `main` when the page lays itself out. */
  flush?: boolean;
  children: React.ReactNode;
  className?: string;
}> = ({
  brand,
  aside,
  progress,
  footer,
  width = 'narrow',
  flush,
  children,
  className,
}) => (
  <div
    className={clsx(
      'mx-auto flex min-h-screen w-full flex-col px-4 pb-10 pt-6 sm:px-6',
      width === 'checkout'
        ? 'max-w-[1040px]'
        : width === 'wide'
          ? 'max-w-[880px]'
          : width === 'form'
            ? 'max-w-[440px]'
            : 'max-w-[600px]',
      className,
    )}
  >
    <header className="flex items-center justify-between gap-4">
      <div className="min-w-0 text-v2-14 font-semibold tracking-[-0.01em]">
        {brand ?? <AskArthurMark />}
      </div>
      {aside && <div className="shrink-0 text-v2-11 text-v2-fg-4">{aside}</div>}
    </header>
    {progress && <div className="mt-5">{progress}</div>}
    <main
      className={clsx(
        'mt-6',
        width !== 'form' && 'flex-1',
        width === 'checkout'
          ? 'border-t border-v2-line pt-6'
          : 'rounded-v2-lg border border-v2-line bg-v2-surface',
        !flush && width !== 'checkout' && 'p-5 sm:p-7',
      )}
    >
      {children}
    </main>
    <footer
      className={clsx(
        'text-v2-11 leading-4 text-v2-fg-4',
        width === 'form' ? 'mt-auto pt-6' : 'mt-6',
      )}
    >
      {footer ?? <PublicFooter />}
    </footer>
  </div>
);

export const SUPPORT_EMAIL = 'support@askarthur.ai';

/** Default footer: legal links and one way to reach a person. */
export const PublicFooter: React.FC<{ supportEmail?: string }> = ({
  supportEmail = SUPPORT_EMAIL,
}) => (
  <div className="flex flex-col gap-1">
    <p>
      © {new Date().getFullYear()} AskArthur ·{' '}
      <Link
        to="/legal/terms"
        className="text-v2-fg-3 underline-offset-2 hover:underline"
      >
        Terms
      </Link>{' '}
      ·{' '}
      <Link
        to="/legal/privacy"
        className="text-v2-fg-3 underline-offset-2 hover:underline"
      >
        Privacy
      </Link>
    </p>
    <p>
      Stuck?{' '}
      <a href={`mailto:${supportEmail}`} className="text-v2-fg-3 underline">
        {supportEmail}
      </a>{' '}
      — a person answers.
    </p>
  </div>
);

/** Page title block: eyebrow (12 fg-3) · h1 (20/28) · lede (14/24 fg-2). */
export const PublicTitle: React.FC<{
  eyebrow?: React.ReactNode;
  children: React.ReactNode;
  lede?: React.ReactNode;
  className?: string;
}> = ({ eyebrow, children, lede, className }) => (
  <div className={className}>
    {eyebrow && <p className="text-v2-12 text-v2-fg-3">{eyebrow}</p>}
    <h1
      className={clsx(
        'text-v2-20 font-semibold tracking-[-0.01em] text-v2-fg',
        eyebrow && 'mt-0.5',
      )}
    >
      {children}
    </h1>
    {lede && <p className="mt-2 text-v2-14 leading-6 text-v2-fg-2">{lede}</p>}
  </div>
);

const publicControl = 'h-10 w-full px-4 text-v2-14 sm:w-auto';

/** The one primary on a public screen: 40px, full width below `sm`. */
export const PublicPrimary: React.FC<ButtonProps> = ({
  className,
  ...rest
}) => (
  <Button
    variant="primary"
    className={clsx(publicControl, className)}
    {...rest}
  />
);

/** The quiet alternative beside it ("Send again", "Back to sign in"). */
export const PublicSecondary: React.FC<ButtonProps> = ({
  className,
  variant = 'ghost',
  ...rest
}) => (
  <Button
    variant={variant}
    className={clsx(publicControl, className)}
    {...rest}
  />
);

/** Action row: stacked (primary on top) below `sm`, a row above. */
export const PublicActions: React.FC<{
  children: React.ReactNode;
  align?: 'end' | 'between' | 'start';
  className?: string;
}> = ({ children, align = 'end', className }) => (
  <div
    className={clsx(
      'flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center',
      align === 'end' && 'sm:justify-end',
      align === 'between' && 'sm:justify-between',
      align === 'start' && 'sm:justify-start',
      className,
    )}
  >
    {children}
  </div>
);

/** A quiet text link in a form's footer ("Back to sign in"). */
export const PublicLink: React.FC<React.ComponentProps<typeof Link>> = ({
  className,
  ...rest
}) => (
  <Link
    className={clsx(
      'rounded-v2-sm text-v2-13 text-v2-fg-2 underline-offset-2 hover:text-v2-fg hover:underline',
      className,
    )}
    {...rest}
  />
);
